import { options } from "preact";

interface NativeFileWriteable {
    write(blob: Blob | string): Promise<void>;
    close(): Promise<void> ;
}

export type FileSystemHandle = NativeFileHandle | NativeDirectoryHandle;

export interface NativeFileHandle {
    readonly kind: "file";
    readonly name: string;

    getFile(): Promise<File>;
    createWritable(): Promise<NativeFileWriteable>;
}

export interface NativeDirectoryHandle {
    readonly kind: "directory";
    readonly name: string;

    entries(): AsyncIterableIterator<[string, FileSystemHandle]>;
    value(): AsyncIterableIterator<FileSystemHandle>;
    keys(): AsyncIterableIterator<string>;
}

export interface FilePickerAcceptType {
    description?: string;
    accept: { [key: string]: string | string[] };
}

interface FilePickerOptions {
    types: FilePickerAcceptType[];
}

interface OpenFilePickerOptions extends FilePickerOptions {
    multiple?: boolean;
}

export interface FilePickerContainer {
    showOpenFilePicker(options?: OpenFilePickerOptions): Promise<NativeFileHandle[]>;
    showDirectoryPicker(): Promise<NativeDirectoryHandle>;
}

export class DynamicFile {
    static get isSupported() {
        return "showOpenFilePicker" in window;
    }

    private fileHandle: NativeFileHandle | null = null;

    get isOpened() {
        return !!this.fileHandle;
    }

    get name() {
        if (!this.fileHandle) {
            throw new Error("File is not opened");
        }

        return this.fileHandle.name;
    }

    constructor(openedFile?: NativeFileHandle) {
        this.fileHandle = openedFile ? openedFile : null;
    }

    async open(types?: FilePickerAcceptType[]) {
        let handle;

        try {
            handle = await this.getFileHandle(types ? {
                types
            } : undefined);
        } catch (e) {
            if (e instanceof Error && "AbortError" !== e.name) {
                console.error(e);
            }
        }

        if (!handle) {
            this.fileHandle = null;
            return;
        }

        this.fileHandle = handle;
    }

    close() {
        this.fileHandle = null;
    }

    async writeFile(data: string) {
        if (!this.fileHandle) {
            throw new Error("File is not opened");
        }

        const writeable = await this.fileHandle.createWritable();
        await writeable.write(data);
        await writeable.close();
    }

    async readFile() {
        if (!this.fileHandle) {
            throw new Error("File is not opened");
        }

        const file = await this.fileHandle.getFile();

        return file;
    }

    async readFileText() {
        if (!this.fileHandle) {
            throw new Error("File is not opened");
        }

        const file = await this.fileHandle.getFile();

        const text = await file.text()

        return text;
    }

    private getFileHandle(options?: OpenFilePickerOptions) {
        return (window as unknown as FilePickerContainer).showOpenFilePicker(options).then((e) => e[0]);
    }
}
